import { z } from 'zod';

import {
  OUTCOME_KEYS,
  PET_LOSS_LOCALES,
  type OutcomeCopyOverrides,
  type PetLossLocale,
  type ReadinessPageOverrides
} from './types';

export const PET_LOSS_LIMITS = {
  minQuestions: 1,
  maxQuestions: 12,
  minOptions: 2,
  maxOptions: 8,
  maxWeight: 3,
  promptLength: 240,
  optionLabelLength: 160,
  badgeLength: 60,
  headingLength: 140,
  outcomeBodyLength: 1200,
  practiceNameLength: 120,
  practitionerNameLength: 120,
  bioLength: 1500,
  introHeadingLength: 120,
  introTextLength: 1500,
  urlLength: 500,
  pageTitleLength: 120,
  stepsHeadingLength: 80,
  stepLabelLength: 40,
  whatsNextTitleLength: 80,
  disclaimerLength: 600,
  calloutTitleLength: 140,
  calloutTextLength: 800,
  maxCalloutLinks: 4,
  linkLabelLength: 80,
  maxNextItems: 6,
  nextItemTitleLength: 100,
  nextItemCategoryLength: 40
} as const;

export const PET_LOSS_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export const PET_LOSS_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;

const weightSchema = z.number().int().min(0).max(PET_LOSS_LIMITS.maxWeight);

const idSchema = z
  .string()
  .regex(PET_LOSS_ID_PATTERN, 'Use lowercase letters, numbers and dashes');

export const readinessOptionSchema = z.object({
  value: idSchema,
  label: z
    .string()
    .trim()
    .min(1, 'Every answer needs text')
    .max(PET_LOSS_LIMITS.optionLabelLength),
  soon: weightSchema,
  foster: weightSchema,
  ready: weightSchema
});

export const readinessQuestionSchema = z
  .object({
    id: idSchema,
    prompt: z
      .string()
      .trim()
      .min(1, 'Every question needs text')
      .max(PET_LOSS_LIMITS.promptLength),
    options: z
      .array(readinessOptionSchema)
      .min(
        PET_LOSS_LIMITS.minOptions,
        'Each question needs at least two answers'
      )
      .max(PET_LOSS_LIMITS.maxOptions)
  })
  .refine(
    (question) =>
      new Set(question.options.map((option) => option.value)).size ===
      question.options.length,
    'Answers within a question must be unique'
  );

export const readinessQuestionsSchema = z
  .array(readinessQuestionSchema)
  .min(PET_LOSS_LIMITS.minQuestions, 'Keep at least one question')
  .max(PET_LOSS_LIMITS.maxQuestions)
  .refine(
    (questions) =>
      new Set(questions.map((question) => question.id)).size ===
      questions.length,
    'Question ids must be unique'
  );

export const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

export const isReadinessHref = (value: string): boolean =>
  isHttpUrl(value) || /^\/(?!\/)/.test(value);

const hrefSchema = z
  .string()
  .trim()
  .min(1, 'Add the web address')
  .max(PET_LOSS_LIMITS.urlLength)
  .refine(isReadinessHref, 'Enter a full address starting with https://');

const imageRefSchema = z
  .string()
  .trim()
  .max(PET_LOSS_LIMITS.urlLength)
  .nullable()
  .transform((value) => (value ? value : null))
  .refine(
    (value) => value === null || isReadinessHref(value),
    'Upload the image again'
  );

export const readinessLinkSchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, 'Give the link a title')
      .max(PET_LOSS_LIMITS.linkLabelLength),
    url: hrefSchema
  })
  .strict();

export const readinessCalloutSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'Give the box a title')
      .max(PET_LOSS_LIMITS.calloutTitleLength),
    text: z.string().trim().max(PET_LOSS_LIMITS.calloutTextLength),
    logoUrl: imageRefSchema,
    links: z
      .array(readinessLinkSchema)
      .max(
        PET_LOSS_LIMITS.maxCalloutLinks,
        `Add at most ${PET_LOSS_LIMITS.maxCalloutLinks} links to the box`
      )
  })
  .strict();

export const readinessNextItemSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'Give the card a title')
      .max(PET_LOSS_LIMITS.nextItemTitleLength),
    category: z.string().trim().max(PET_LOSS_LIMITS.nextItemCategoryLength),
    url: hrefSchema,
    imageUrl: imageRefSchema
  })
  .strict();

const outcomeCopyOverrideSchema = z
  .object({
    badge: z.string().trim().max(PET_LOSS_LIMITS.badgeLength).optional(),
    heading: z.string().trim().max(PET_LOSS_LIMITS.headingLength).optional(),
    body: z.string().trim().max(PET_LOSS_LIMITS.outcomeBodyLength).optional(),
    callout: readinessCalloutSchema.nullable().optional(),
    whatsNext: z
      .array(readinessNextItemSchema)
      .max(
        PET_LOSS_LIMITS.maxNextItems,
        `Add at most ${PET_LOSS_LIMITS.maxNextItems} cards`
      )
      .optional()
  })
  .strict();

export const outcomeCopyOverridesSchema = z
  .object({
    soon: outcomeCopyOverrideSchema.optional(),
    foster: outcomeCopyOverrideSchema.optional(),
    ready: outcomeCopyOverrideSchema.optional()
  })
  .strict();

const requiredLine = (max: number, message: string) =>
  z.string().trim().min(1, message).max(max);

const stepLabelSchema = requiredLine(
  PET_LOSS_LIMITS.stepLabelLength,
  'Every area needs a name'
);

export const readinessPageOverridesSchema = z
  .object({
    pageTitle: requiredLine(
      PET_LOSS_LIMITS.pageTitleLength,
      'Add a page title'
    ).optional(),
    stepsVisible: z.boolean().optional(),
    stepsHeading: requiredLine(
      PET_LOSS_LIMITS.stepsHeadingLength,
      'Add a heading'
    ).optional(),
    stepLabels: z
      .tuple([
        stepLabelSchema,
        stepLabelSchema,
        stepLabelSchema,
        stepLabelSchema,
        stepLabelSchema
      ])
      .optional(),
    whatsNextTitle: requiredLine(
      PET_LOSS_LIMITS.whatsNextTitleLength,
      'Add a heading'
    ).optional(),
    disclaimer: requiredLine(
      PET_LOSS_LIMITS.disclaimerLength,
      'Add a note'
    ).optional()
  })
  .strict();

export function readOutcomeOverrides(raw: unknown): OutcomeCopyOverrides {
  if (!raw || typeof raw !== 'object') return {};
  const overrides: OutcomeCopyOverrides = {};
  for (const key of OUTCOME_KEYS) {
    const parsed = outcomeCopyOverrideSchema
      .strip()
      .safeParse((raw as Record<string, unknown>)[key]);
    if (parsed.success) overrides[key] = parsed.data;
  }
  return overrides;
}

export function readPageOverrides(raw: unknown): ReadinessPageOverrides {
  const parsed = readinessPageOverridesSchema.strip().safeParse(raw ?? {});
  return parsed.success ? parsed.data : {};
}

export const petLossLocaleSchema = z.enum(
  PET_LOSS_LOCALES as unknown as [PetLossLocale, ...PetLossLocale[]]
);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value ? value : null));

const optionalUrl = (check: (value: string) => boolean, message: string) =>
  z
    .string()
    .trim()
    .max(PET_LOSS_LIMITS.urlLength)
    .nullish()
    .transform((value) => (value ? value : null))
    .refine((value) => value === null || check(value), message);

export const petLossPartnerProfileSchema = z.object({
  practiceName: z
    .string()
    .trim()
    .min(1, 'A practice name is required')
    .max(PET_LOSS_LIMITS.practiceNameLength),
  practitionerName: optionalText(PET_LOSS_LIMITS.practitionerNameLength),
  bio: optionalText(PET_LOSS_LIMITS.bioLength),
  logoUrl: optionalUrl(isHttpUrl, 'Upload the logo again'),
  headshotUrl: optionalUrl(isHttpUrl, 'Upload the photo again'),
  introHeading: optionalText(PET_LOSS_LIMITS.introHeadingLength),
  introText: optionalText(PET_LOSS_LIMITS.introTextLength),
  introImageUrl: optionalUrl(isHttpUrl, 'Upload the image again'),
  pageCopy: readinessPageOverridesSchema.default({}),
  locale: petLossLocaleSchema,
  questions: readinessQuestionsSchema,
  outcomes: outcomeCopyOverridesSchema
});

export type PetLossPartnerProfileInput = z.input<
  typeof petLossPartnerProfileSchema
>;
export type PetLossPartnerProfile = z.output<
  typeof petLossPartnerProfileSchema
>;

export const petLossAnswersSchema = z
  .record(z.string().max(40), z.string().max(40))
  .refine(
    (answers) => Object.keys(answers).length <= PET_LOSS_LIMITS.maxQuestions,
    'Too many answers'
  );
